import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  TrendingUp, TrendingDown, BarChart3, Target, Award, Layers,
  RefreshCw, ChevronDown, ChevronUp, ArrowRight, Megaphone,
  Users, Grid3X3, Filter, GitBranch, IndianRupee
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Layout from "../components/Layout";
import { API, useProject } from "../App";
import { toast } from "sonner";
import { DateRangePicker, openLeadsTab } from "../components/DateRangePicker";
import { appendDateParams } from "../utils/dateFilters";
import { last30Days } from "../lib/dateDefaults";
import { useUrlFilters } from "../hooks/useUrlFilters";

const _DEF = last30Days();
import { InfoTip } from "../components/InfoTip";
import CostTrackingTab from "../components/CostTrackingTab";

const STAGE_COLORS = {
  new: "#94a3b8", contacted: "#3b82f6", site_visit_scheduled: "#8b5cf6",
  site_visit: "#a855f7", revisit: "#d946ef", negotiation: "#f59e0b",
  booked: "#10b981", lost: "#ef4444",
};
const STAGE_LABELS = {
  new: "New", contacted: "Contacted", site_visit_scheduled: "SV Scheduled",
  site_visit: "Site Visit", revisit: "Revisit", negotiation: "Negotiation",
  booked: "Booked", lost: "Lost",
};

const ConvBadge = ({ rate }) => {
  const cls = rate >= 10 ? "bg-emerald-100 text-emerald-700" :
              rate >= 5 ? "bg-blue-100 text-blue-700" :
              rate > 0 ? "bg-amber-100 text-amber-700" :
              "bg-slate-100 text-slate-500";
  return <Badge variant="outline" className={`${cls} font-mono text-xs`}>{rate}%</Badge>;
};

const MiniBar = ({ value, max, color = "bg-blue-500" }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-full bg-slate-100 rounded-full h-1.5">
      <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${Math.max(pct, 2)}%` }} />
    </div>
  );
};

const FunnelBar = ({ label, value, total, color }) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-600 w-24 shrink-0">{label}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
        <div className="h-full rounded-full transition-all flex items-center justify-end pr-2"
          style={{ width: `${Math.max(pct, 3)}%`, backgroundColor: color }}>
          {pct > 12 && <span className="text-[10px] font-bold text-white">{value}</span>}
        </div>
      </div>
      <span className="text-xs font-mono text-slate-600 w-10 text-right">{value}</span>
    </div>
  );
};

const CampaignAnalyticsPage = () => {
  const { selectedProjectId } = useProject();
  const [sourceData, setSourceData] = useState(null);
  const [roiData, setRoiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [urlFilters, setUrlFilters] = useUrlFilters({
    date_from: _DEF.from,
    date_to: _DEF.to,
    tab: "campaigns",
    sort_by: "total",
  });
  const dateFrom = urlFilters.date_from;
  const setDateFrom = (v) => setUrlFilters({ date_from: v });
  const dateTo = urlFilters.date_to;
  const setDateTo = (v) => setUrlFilters({ date_to: v });
  const activeTab = urlFilters.tab;
  const setActiveTab = (v) => setUrlFilters({ tab: v });
  const sortBy = urlFilters.sort_by;
  const setSortBy = (v) => setUrlFilters({ sort_by: v });
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [expandedSource, setExpandedSource] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const dp = new URLSearchParams();
      appendDateParams(dp, dateFrom, dateTo);
      if (selectedProjectId !== "all") dp.set("project_id", selectedProjectId);
      const qs = dp.toString();
      const suffix = qs ? `?${qs}` : "";
      const [srcRes, roiRes] = await Promise.all([
        axios.get(`${API}/analytics/campaign-source${suffix}`),
        axios.get(`${API}/routing/roi${suffix}`),
      ]);
      setSourceData(srcRes.data);
      setRoiData(roiRes.data);
    } catch {
      toast.error("Failed to load campaign analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [dateFrom, dateTo, selectedProjectId]);

  const sortedCampaigns = useMemo(() => {
    if (!sourceData?.campaigns) return [];
    const list = [...sourceData.campaigns];
    if (sortBy === "total") list.sort((a, b) => b.total - a.total);
    else if (sortBy === "conversion") list.sort((a, b) => b.conversion_rate - a.conversion_rate);
    else if (sortBy === "booked") list.sort((a, b) => b.booked - a.booked);
    else if (sortBy === "loss") list.sort((a, b) => b.loss_rate - a.loss_rate);
    return list;
  }, [sourceData, sortBy]);

  if (loading || !sourceData) {
    return (
      <Layout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-72" />
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {[...Array(6)].map((_, i) => <div key={i} className="h-24 bg-slate-200 rounded-lg" />)}
            </div>
            <div className="h-96 bg-slate-200 rounded-lg" />
          </div>
        </div>
      </Layout>
    );
  }

  const { campaigns, sources, cross_tab, summary } = sourceData;
  const ruleEffectiveness = roiData?.rule_effectiveness || [];
  const teamPaths = roiData?.team_paths || [];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="campaign-analytics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Campaign Analytics</h1>
            <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
              Track campaign performance, source ROI, and routing effectiveness
              <InfoTip text="Unified view of marketing campaign performance, lead source conversion rates, source×campaign cross-analysis, routing rule effectiveness, and team path outcomes." />
            </p>
          </div>
          <div className="flex items-center gap-2">
            <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-campaign-analytics">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openLeadsTab({ date_from: dateFrom, date_to: dateTo })}>
            <CardContent className="p-4 text-center">
              <Layers className="w-5 h-5 mx-auto mb-1 text-slate-500" />
              <p className="text-2xl font-bold text-slate-900" data-testid="ca-total-leads">{summary.total_leads}</p>
              <p className="text-xs text-slate-500">Total Leads</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Megaphone className="w-5 h-5 mx-auto mb-1 text-blue-500" />
              <p className="text-2xl font-bold text-blue-600" data-testid="ca-with-campaign">{summary.leads_with_campaign}</p>
              <p className="text-xs text-slate-500">With Campaign</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openLeadsTab({ stage: "booked", date_from: dateFrom, date_to: dateTo })}>
            <CardContent className="p-4 text-center">
              <Target className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
              <p className="text-2xl font-bold text-emerald-600" data-testid="ca-total-booked">{summary.total_booked}</p>
              <p className="text-xs text-slate-500">Booked</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-5 h-5 mx-auto mb-1 text-violet-500" />
              <p className="text-2xl font-bold text-violet-600">{summary.overall_conversion}%</p>
              <p className="text-xs text-slate-500">Conversion</p>
            </CardContent>
          </Card>
          {summary.best_campaign && (
            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardContent className="p-4 text-center">
                <Award className="w-5 h-5 mx-auto mb-1 text-emerald-600" />
                <p className="text-sm font-bold text-emerald-700 truncate" data-testid="ca-best-campaign" title={summary.best_campaign}>{summary.best_campaign}</p>
                <p className="text-xs text-emerald-600">{summary.best_conversion}% conv</p>
              </CardContent>
            </Card>
          )}
          {summary.worst_campaign && (
            <Card className="border-red-200 bg-red-50/30">
              <CardContent className="p-4 text-center">
                <TrendingDown className="w-5 h-5 mx-auto mb-1 text-red-500" />
                <p className="text-sm font-bold text-red-700 truncate" title={summary.worst_campaign}>{summary.worst_campaign}</p>
                <p className="text-xs text-red-600">{summary.worst_conversion}% conv</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="flex-wrap h-auto gap-1">
            <TabsTrigger value="campaigns" data-testid="ca-tab-campaigns">
              <Megaphone className="w-3.5 h-3.5 mr-1.5" /> Campaigns ({summary.distinct_campaigns})
            </TabsTrigger>
            <TabsTrigger value="sources" data-testid="ca-tab-sources">
              <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Sources ({summary.distinct_sources})
            </TabsTrigger>
            <TabsTrigger value="cross" data-testid="ca-tab-cross">
              <Grid3X3 className="w-3.5 h-3.5 mr-1.5" /> Source x Campaign
            </TabsTrigger>
            <TabsTrigger value="rules" data-testid="ca-tab-rules">
              <GitBranch className="w-3.5 h-3.5 mr-1.5" /> Rule Effectiveness
            </TabsTrigger>
            <TabsTrigger value="paths" data-testid="ca-tab-paths">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Team Paths
            </TabsTrigger>
            <TabsTrigger value="costs" data-testid="ca-tab-costs">
              <IndianRupee className="w-3.5 h-3.5 mr-1.5" /> Cost Tracking
            </TabsTrigger>
          </TabsList>

          {/* ── CAMPAIGNS TAB ── */}
          <TabsContent value="campaigns" className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-xs text-slate-500">Sort by:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40 h-8 text-xs" data-testid="ca-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="total">Lead Volume</SelectItem>
                  <SelectItem value="conversion">Conversion Rate</SelectItem>
                  <SelectItem value="booked">Bookings</SelectItem>
                  <SelectItem value="loss">Loss Rate</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {sortedCampaigns.length === 0 ? (
              <Card><CardContent className="p-12 text-center text-slate-400">
                <Megaphone className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No campaign data found. Import campaign data for your leads.</p>
              </CardContent></Card>
            ) : sortedCampaigns.map((camp) => (
              <Card key={camp.campaign} className="transition-shadow hover:shadow-md" data-testid={`ca-campaign-${camp.campaign}`}>
                <CardContent className="p-0">
                  <div className="flex items-center gap-4 p-4 cursor-pointer"
                    onClick={() => setExpandedCampaign(expandedCampaign === camp.campaign ? null : camp.campaign)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900">{camp.campaign}</h3>
                        <Badge variant="outline" className="text-xs bg-slate-50">{camp.total} leads</Badge>
                      </div>
                      <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-500">
                        <span className="font-mono font-bold text-slate-700">{camp.total}</span>
                        <ArrowRight className="w-3 h-3" />
                        <span className="font-mono">{camp.contacted}</span> contacted
                        <ArrowRight className="w-3 h-3" />
                        <span className="font-mono">{camp.site_visits}</span> visits
                        <ArrowRight className="w-3 h-3" />
                        <span className="font-mono font-bold text-emerald-600">{camp.booked}</span> booked
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Conversion</p>
                        <ConvBadge rate={camp.conversion_rate} />
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Lost</p>
                        <span className="text-sm font-mono text-red-500">{camp.loss_rate}%</span>
                      </div>
                      {expandedCampaign === camp.campaign
                        ? <ChevronUp className="w-4 h-4 text-slate-400" />
                        : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>

                  {expandedCampaign === camp.campaign && (
                    <div className="border-t px-4 pb-4 pt-3 bg-slate-50/50">
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                        <div><p className="text-xs text-slate-500 mb-0.5">Total Leads</p><p className="text-lg font-bold text-slate-900">{camp.total}</p></div>
                        <div><p className="text-xs text-slate-500 mb-0.5">Contact Rate</p><p className="text-lg font-bold text-blue-600">{camp.contact_rate}%</p></div>
                        <div><p className="text-xs text-slate-500 mb-0.5">Conversion Rate</p><p className="text-lg font-bold text-emerald-600">{camp.conversion_rate}%</p></div>
                        <div><p className="text-xs text-slate-500 mb-0.5">Loss Rate</p><p className="text-lg font-bold text-red-500">{camp.loss_rate}%</p></div>
                        <div><p className="text-xs text-slate-500 mb-0.5">Avg Score</p><p className="text-lg font-bold text-amber-600">{camp.avg_score}</p></div>
                      </div>
                      <div className="space-y-1.5 mb-4">
                        {Object.entries(camp.stage_breakdown).map(([stage, count]) => (
                          <FunnelBar key={stage} label={STAGE_LABELS[stage] || stage} value={count} total={camp.total} color={STAGE_COLORS[stage] || "#94a3b8"} />
                        ))}
                      </div>
                      {Object.keys(camp.top_sources).length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-600 mb-2">Top Lead Sources</p>
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(camp.top_sources).map(([src, cnt]) => (
                              <Badge key={src} variant="secondary" className="text-xs">
                                {src} <span className="ml-1 font-mono text-slate-500">({cnt})</span>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* ── SOURCES TAB ── */}
          <TabsContent value="sources" className="space-y-3">
            {sources.length === 0 ? (
              <Card><CardContent className="p-12 text-center text-slate-400">
                <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No source data found.</p>
              </CardContent></Card>
            ) : sources.map((src) => (
              <Card key={src.source} className="transition-shadow hover:shadow-md" data-testid={`ca-source-${src.source}`}>
                <CardContent className="p-0">
                  <div className="flex items-center gap-4 p-4 cursor-pointer"
                    onClick={() => setExpandedSource(expandedSource === src.source ? null : src.source)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 capitalize">{src.source}</h3>
                        <Badge variant="outline" className="text-xs bg-slate-50">{src.total} leads</Badge>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500">
                        <span className="text-emerald-600 font-mono">{src.booked} booked</span>
                        <span className="text-slate-300">|</span>
                        <span className="text-red-500 font-mono">{src.lost} lost</span>
                        <span className="text-slate-300">|</span>
                        <span className="font-mono">{src.contacted} contacted</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Conversion</p>
                        <ConvBadge rate={src.conversion_rate} />
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Avg Score</p>
                        <span className="text-sm font-mono text-amber-600">{src.avg_score}</span>
                      </div>
                      {expandedSource === src.source
                        ? <ChevronUp className="w-4 h-4 text-slate-400" />
                        : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>
                  {expandedSource === src.source && (
                    <div className="border-t px-4 pb-4 pt-3 bg-slate-50/50">
                      {/* Sub-source breakdown */}
                      {src.sub_source_breakdown && src.sub_source_breakdown.filter(s => s.sub_source !== "(direct)").length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs font-medium text-slate-600 mb-2">Sub-source Breakdown</p>
                          <Table>
                            <TableHeader>
                              <TableRow className="text-xs">
                                <TableHead className="py-1.5">Sub-source</TableHead>
                                <TableHead className="text-right py-1.5">Leads</TableHead>
                                <TableHead className="text-right py-1.5">Booked</TableHead>
                                <TableHead className="text-right py-1.5">Lost</TableHead>
                                <TableHead className="text-right py-1.5">Conv%</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {src.sub_source_breakdown.filter(s => s.sub_source !== "(direct)").map((sub) => (
                                <TableRow key={sub.sub_source} className="text-xs cursor-pointer hover:bg-slate-100"
                                  data-testid={`subsource-row-${sub.sub_source}`}
                                  onClick={() => openLeadsTab({ source: src.source, sub_source: sub.sub_source })}>
                                  <TableCell className="py-1.5 font-medium">{sub.sub_source}</TableCell>
                                  <TableCell className="text-right py-1.5 font-mono">{sub.total}</TableCell>
                                  <TableCell className="text-right py-1.5 font-mono text-emerald-600">{sub.booked}</TableCell>
                                  <TableCell className="text-right py-1.5 font-mono text-red-500">{sub.lost}</TableCell>
                                  <TableCell className="text-right py-1.5"><ConvBadge rate={sub.conversion_rate} /></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                      <div className="space-y-1.5 mb-4">
                        {Object.entries(src.stage_breakdown).map(([stage, count]) => (
                          <FunnelBar key={stage} label={STAGE_LABELS[stage] || stage} value={count} total={src.total} color={STAGE_COLORS[stage] || "#94a3b8"} />
                        ))}
                      </div>
                      {Object.keys(src.top_campaigns).length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-600 mb-2">Top Campaigns</p>
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(src.top_campaigns).map(([camp, cnt]) => (
                              <Badge key={camp} variant="secondary" className="text-xs">
                                {camp} <span className="ml-1 font-mono text-slate-500">({cnt})</span>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* ── SOURCE × CAMPAIGN CROSS-TAB ── */}
          <TabsContent value="cross">
            <Card>
              <CardHeader><CardTitle className="text-base">Top Source x Campaign Combinations</CardTitle></CardHeader>
              <CardContent className="p-0">
                {cross_tab.length === 0 ? (
                  <div className="p-12 text-center text-slate-400">
                    <Grid3X3 className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No cross-tab data available.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Source</TableHead>
                        <TableHead>Campaign</TableHead>
                        <TableHead className="text-right">Leads</TableHead>
                        <TableHead className="text-right">Booked</TableHead>
                        <TableHead className="text-right">Lost</TableHead>
                        <TableHead className="text-right">Conversion</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cross_tab.map((row, i) => (
                        <TableRow key={i} data-testid={`ca-cross-${i}`}>
                          <TableCell className="font-medium capitalize">{row.source}</TableCell>
                          <TableCell className="text-slate-700">{row.campaign}</TableCell>
                          <TableCell className="text-right font-mono">{row.total}</TableCell>
                          <TableCell className="text-right font-mono text-emerald-600">{row.booked}</TableCell>
                          <TableCell className="text-right font-mono text-red-500">{row.lost}</TableCell>
                          <TableCell className="text-right"><ConvBadge rate={row.conversion_rate} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── RULE EFFECTIVENESS TAB ── */}
          <TabsContent value="rules">
            <Card>
              <CardContent className="p-0">
                {ruleEffectiveness.length === 0 ? (
                  <div className="p-12 text-center text-slate-400">
                    <GitBranch className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p>No routing rules have processed leads yet. Rules will appear here once lead routing is active.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rule</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead className="text-right">Routed</TableHead>
                        <TableHead className="text-right">Active</TableHead>
                        <TableHead className="text-right">Booked</TableHead>
                        <TableHead className="text-right">Lost</TableHead>
                        <TableHead className="text-right">Conversion</TableHead>
                        <TableHead>Performance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ruleEffectiveness.map((rule) => (
                        <TableRow key={rule.rule_id} data-testid={`ca-rule-${rule.rule_id}`}>
                          <TableCell className="font-medium text-slate-900">{rule.rule_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-xs capitalize">{rule.assignment_method?.replace(/_/g, " ")}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">{rule.leads_routed}</TableCell>
                          <TableCell className="text-right font-mono text-blue-600">{rule.active_leads}</TableCell>
                          <TableCell className="text-right font-mono text-emerald-600">{rule.booked}</TableCell>
                          <TableCell className="text-right font-mono text-red-500">{rule.lost}</TableCell>
                          <TableCell className="text-right"><ConvBadge rate={rule.conversion_rate} /></TableCell>
                          <TableCell className="w-32">
                            <MiniBar value={rule.conversion_rate} max={Math.max(...ruleEffectiveness.map(r => r.conversion_rate), 1)} color="bg-emerald-500" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── TEAM PATHS TAB ── */}
          <TabsContent value="paths">
            <Card>
              <CardHeader><CardTitle className="text-base">Campaign + Team Combinations — Ranked by Conversion</CardTitle></CardHeader>
              <CardContent className="p-0">
                {teamPaths.length === 0 ? (
                  <div className="p-12 text-center text-slate-400">
                    <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p>No campaign-team paths found. Assign leads to teams via campaigns to see path data.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Campaign</TableHead>
                        <TableHead>Team</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Leads</TableHead>
                        <TableHead className="text-right">Booked</TableHead>
                        <TableHead className="text-right">Conversion</TableHead>
                        <TableHead>Performance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {teamPaths.map((path, i) => (
                        <TableRow key={i} data-testid={`ca-path-${i}`}>
                          <TableCell className="font-medium">{path.campaign}</TableCell>
                          <TableCell>{path.team}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs capitalize">{path.team_type}</Badge></TableCell>
                          <TableCell className="text-right font-mono">{path.total_leads}</TableCell>
                          <TableCell className="text-right font-mono text-emerald-600">{path.booked}</TableCell>
                          <TableCell className="text-right"><ConvBadge rate={path.conversion_rate} /></TableCell>
                          <TableCell className="w-32">
                            <MiniBar value={path.conversion_rate} max={Math.max(...teamPaths.map(p => p.conversion_rate), 1)} color="bg-blue-500" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── COST TRACKING TAB ── */}
          <TabsContent value="costs">
            <CostTrackingTab campaignNames={campaigns.map(c => c.campaign)} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default CampaignAnalyticsPage;
